import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Invoice, Settings, Company, Schedule } from '@dutchinvoice/types';
import { adminApi } from '../lib/api';

interface User {
  uid: string;
  email: string;
  defaultCompany: string | null;
  createdAt: number;
  lastActive: number;
  invoiceCount: number;
  settings: Settings;
}

interface EmailQueueItem {
  id: string;
  invoice_id: string;
  user_id: string;
  user_email: string;
  invoice_number: string;
  recipient_email: string;
  scheduled_at: string;
  sent_at?: string;
  status: 'planned' | 'sent' | 'failed';
  attempts: number;
  error?: string;
  email_locale: string;
  custom_message?: string;
  created_at: number;
  updated_at: number;
}

interface AdminStore {
  // Users state
  users: User[];
  loading: boolean;
  error: string | null;

  // Selected user state
  selectedUser: User | null;

  // User invoices state
  userInvoices: Invoice[];
  userInvoicesLoading: boolean;
  userInvoicesError: string | null;
  currentPage: number;
  hasMore: boolean;

  // User companies state
  userCompanies: Company[];
  userCompaniesLoading: boolean;
  userCompaniesError: string | null;

  // User settings state
  userSettings: Settings | null;
  userSettingsLoading: boolean;
  userSettingsError: string | null;

  // User schedules state
  userSchedules: Schedule[];
  userSchedulesLoading: boolean;
  userSchedulesError: string | null;

  // Email queue state
  emailQueue: EmailQueueItem[];
  emailQueueLoading: boolean;
  emailQueueError: string | null;

  // Users actions
  fetchUsers: () => Promise<void>;
  fetchUserDetails: (uid: string) => Promise<void>;

  // User invoices actions
  fetchUserInvoices: (uid: string) => Promise<void>;
  loadMoreUserInvoices: (uid: string) => Promise<void>;

  // User companies actions
  fetchUserCompanies: (uid: string) => Promise<void>;

  // User settings actions
  fetchUserSettings: (uid: string) => Promise<void>;

  // User schedules actions
  fetchUserSchedules: (uid: string) => Promise<void>;

  // Email queue actions
  fetchEmailQueue: (limit?: number) => Promise<void>;
}

export const useAdminStore = create<AdminStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      users: [],
      loading: false,
      error: null,
      selectedUser: null,

      // User invoices state
      userInvoices: [],
      userInvoicesLoading: false,
      userInvoicesError: null,
      currentPage: 1,
      hasMore: false,

      // User companies state
      userCompanies: [],
      userCompaniesLoading: false,
      userCompaniesError: null,

      // User settings state
      userSettings: null,
      userSettingsLoading: false,
      userSettingsError: null,

      // User schedules state
      userSchedules: [],
      userSchedulesLoading: false,
      userSchedulesError: null,

      // Email queue state
      emailQueue: [],
      emailQueueLoading: false,
      emailQueueError: null,

      // Fetch users
      fetchUsers: async () => {
        set({ loading: true, error: null });
        try {
          const { users } = await adminApi.getUsers();
          set({ users, loading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch users',
            loading: false
          });
        }
      },

      // Fetch user details
      fetchUserDetails: async (uid: string) => {
        try {
          console.log('[AdminStore] fetchUserDetails called with uid:', uid);

          // First check if we already have the user in our local state
          const user = get().users.find(u => u.uid === uid);
          if (user) {
            console.log('[AdminStore] User found in local state:', user);

            // Ensure user has settings
            if (!user.settings) {
              user.settings = {
                language: 'en',
                country: 'NL',
                invoiceStartNumber: 1,
                latestInvoiceNumber: 0,
                invoiceNumberFormat: {
                  prefix: 'INV',
                  includeYear: true,
                  yearFormat: 'full',
                  separator: '-',
                  numberPadding: 4
                }
              };
            }

            set({ selectedUser: user });
            return;
          }

          console.log('[AdminStore] User not found in local state, fetching all users');

          // If we don't have the user, fetch all users first
          // This is more reliable than the single user endpoint which might not be deployed yet
          const { users } = await adminApi.getUsers();
          console.log('[AdminStore] All users fetched:', users);

          // Ensure all users have settings
          const processedUsers = users.map((u: Omit<User, 'settings'> & { settings?: Partial<Settings> }) => ({
            ...u,
            settings: u.settings || {
              language: 'en',
              country: 'NL',
              invoiceStartNumber: 1,
              latestInvoiceNumber: 0,
              invoiceNumberFormat: {
                prefix: 'INV',
                includeYear: true,
                yearFormat: 'full',
                separator: '-',
                numberPadding: 4
              }
            }
          }));

          // Update our users array
          set({ users: processedUsers });

          // Find the user in the fetched users
          const foundUser = processedUsers.find((u: User) => u.uid === uid);
          if (foundUser) {
            console.log('[AdminStore] User found in fetched users:', foundUser);
            set({ selectedUser: foundUser });
          } else {
            console.error('[AdminStore] User not found in fetched users');
            set({ error: `User with ID ${uid} not found` });
          }
        } catch (error) {
          console.error('[AdminStore] Error in fetchUserDetails:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch user details',
            selectedUser: null
          });
        }
      },

      // Fetch user invoices
      fetchUserInvoices: async (uid: string) => {
        set({ userInvoicesLoading: true, userInvoicesError: null, currentPage: 1 });
        try {
          const { invoices, hasMore } = await adminApi.getUserInvoices(uid, 1);
          set({
            userInvoices: invoices,
            userInvoicesLoading: false,
            hasMore
          });
        } catch (error) {
          set({
            userInvoicesError: error instanceof Error ? error.message : 'Failed to fetch invoices',
            userInvoicesLoading: false
          });
        }
      },

      // Load more user invoices
      loadMoreUserInvoices: async (uid: string) => {
        const { currentPage, userInvoices } = get();
        const nextPage = currentPage + 1;

        set({ userInvoicesLoading: true });
        try {
          const { invoices, hasMore } = await adminApi.getUserInvoices(uid, nextPage);
          set({
            userInvoices: [...userInvoices, ...invoices],
            currentPage: nextPage,
            hasMore,
            userInvoicesLoading: false
          });
        } catch (error) {
          set({
            userInvoicesError: error instanceof Error ? error.message : 'Failed to load more invoices',
            userInvoicesLoading: false
          });
        }
      },

      // Fetch user companies
      fetchUserCompanies: async (uid: string) => {
        set({ userCompaniesLoading: true, userCompaniesError: null });
        try {
          const { companies } = await adminApi.getUserCompanies(uid);
          set({
            userCompanies: companies,
            userCompaniesLoading: false
          });
        } catch (error) {
          set({
            userCompaniesError: error instanceof Error ? error.message : 'Failed to fetch companies',
            userCompaniesLoading: false
          });
        }
      },

      // Fetch user settings
      fetchUserSettings: async (uid: string) => {
        set({ userSettingsLoading: true, userSettingsError: null });
        try {
          const { settings } = await adminApi.getUserSettings(uid);
          set({
            userSettings: settings,
            userSettingsLoading: false
          });
        } catch (error) {
          set({
            userSettingsError: error instanceof Error ? error.message : 'Failed to fetch settings',
            userSettingsLoading: false
          });
        }
      },

      // Fetch user schedules
      fetchUserSchedules: async (uid: string) => {
        set({ userSchedulesLoading: true, userSchedulesError: null });
        try {
          const { schedules } = await adminApi.getUserSchedules(uid);
          set({
            userSchedules: schedules,
            userSchedulesLoading: false
          });
        } catch (error) {
          set({
            userSchedulesError: error instanceof Error ? error.message : 'Failed to fetch schedules',
            userSchedulesLoading: false
          });
        }
      },

      // Fetch email queue
      fetchEmailQueue: async (limit = 100) => {
        set({ emailQueueLoading: true, emailQueueError: null });
        try {
          const { emails } = await adminApi.getEmailQueue(limit);
          set({
            emailQueue: emails,
            emailQueueLoading: false
          });
        } catch (error) {
          set({
            emailQueueError: error instanceof Error ? error.message : 'Failed to fetch email queue',
            emailQueueLoading: false
          });
        }
      }
    }),
    { name: 'admin-store' }
  )
); 